import {
  GenerateAccessTokenParams,
  GenerateAccessTokenQueryResult,
} from '@/api/auth';
import { isAccessTokenValid } from '@/helpers/auth';
import { useAuth, setDepartmentAccessFetcher } from '@/stores/auth';
import { useNavigate, useParams } from '@tanstack/react-router';
import Axios, { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { useMemo } from 'react';
import { DepartmentAccessDTO } from '@/api/trafikportalen';

let pendingRequest: { profileId: string; promise: Promise<string> } | null =
  null;

const isSelectedProfile = (profileId: string) =>
  useAuth.getState().userProfile?.id === profileId;

const getAccessToken = (
  authAxios: ReturnType<typeof Axios.create>,
  params: GenerateAccessTokenParams,
  signal?: AbortSignal,
) =>
  authAxios<GenerateAccessTokenQueryResult>({
    url: `/profiles/token`,
    method: 'GET',
    params,
    signal,
  }).then(({ data }) => data);

const useAuthAxiosInstance = () => {
  const { auth } = useConfig();

  return useMemo(
    () =>
      Axios.create({
        baseURL: auth.baseUrl,
        withCredentials: auth.withCredentials,
      }),
    [auth.baseUrl, auth.withCredentials],
  );
};

const createDepartmentAccessFetcher = (
  alltixBaseUrl: string,
  getAccessToken: () => Promise<string>,
) => {
  return async (): Promise<DepartmentAccessDTO> => {
    const accessToken = await getAccessToken();
    const axios = Axios.create({
      baseURL: alltixBaseUrl,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const response = await axios.get<DepartmentAccessDTO>(
      '/departments/access',
    );
    return response.data;
  };
};

export const useAccessToken = () => {
  const { tenantId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const { auth, baseUrl } = useConfig();
  const authAxios = useAuthAxiosInstance();

  const audience = useMemo(() => auth.audience, [auth.audience]);

  const getAccessTokenFn = useCallback(async (): Promise<string> => {
    const redirectToLogin = () => {
      if (!tenantId) {
        navigate({ to: '/' });
        return;
      }
      // Already on the way to login (e.g. an inactivity logout). Navigating
      // again would drop the returnTo that brings the user back afterwards.
      if (/\/login(?:[/?#]|$)/.test(window.location.pathname)) {
        return;
      }
      navigate({
        to: '/$tenantId/login',
        params: { tenantId },
        search: { returnTo: window.location.toString() },
      });
    };

    const { accessToken, userProfile: currentUserProfile } = useAuth.getState();

    // Valid token? Return it
    if (isAccessTokenValid(accessToken)) {
      console.debug('Using cached access token');
      return accessToken ?? '';
    }

    // Without a selected profile (e.g. right after an inactivity logout
    // cleared the auth store) the backend would reject the request with
    // an `invalid UUID length: 0` error on `user_profile_id`. Short-circuit
    // before we even fire the request and bounce to the login page so the
    // user can re-select a profile.
    if (!currentUserProfile?.id) {
      useAuth.getState().logout();
      redirectToLogin();
      throw new Error('No user profile selected');
    }

    const profileId = currentUserProfile.id;

    // Active request for this profile? Return it
    if (pendingRequest?.profileId === profileId) {
      console.debug('Using pending access token request');
      return pendingRequest.promise;
    }

    console.debug('Requesting new access token');
    // The profile can be switched while the request is in flight (e.g. when
    // leaving impersonation). Its outcome must then not touch the auth store,
    // or the new profile ends up with the old profile's permissions.
    const request = getAccessToken(authAxios, {
      audience,
      user_profile_id: profileId,
    })
      .then(({ access_token }) => {
        if (isSelectedProfile(profileId)) {
          setAccessToken(access_token);
        }
        return access_token;
      })
      .catch((error) => {
        if (!isSelectedProfile(profileId)) {
          throw error;
        }
        useAuth.getState().logout();
        console.error('Failed to retrieve access token', error);
        redirectToLogin();
        throw new Error('Failed to retrieve access token');
      })
      .finally(() => {
        if (pendingRequest?.promise === request) {
          console.debug('Clearing access token request');
          pendingRequest = null;
        }
      });

    pendingRequest = { profileId, promise: request };
    console.debug('Using new access token request');
    return request;
  }, [navigate, setAccessToken, audience, authAxios, tenantId]);

  useEffect(() => {
    const departmentAccessFetcher = createDepartmentAccessFetcher(
      baseUrl,
      getAccessTokenFn,
    );
    setDepartmentAccessFetcher(departmentAccessFetcher);
  }, [baseUrl, getAccessTokenFn]);

  return getAccessTokenFn;
};

export const useAxiosAuth = <T>(): ((
  config: AxiosRequestConfig,
) => Promise<T>) => {
  const authAxios = useAuthAxiosInstance();

  return (config: AxiosRequestConfig) => {
    const source = Axios.CancelToken.source();
    const promise = authAxios({
      ...config,
      cancelToken: source.token,
    }).then(({ data }) => data);

    // @ts-expect-error allow cancellations
    promise.cancel = () => {
      console.debug('Query was cancelled by React Query');
      source.cancel('Query was cancelled by React Query');
    };

    return promise;
  };
};
